import { SettingTabsService } from '@abp/ng.setting-management/config';
import {
  ChangeDetectionStrategy,
  AfterViewInit,
  Component,
  OnInit,
  Renderer2,
  computed,
  inject,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';
import { GeneralSettingsComponent } from './general-settings/general-settings.component';
import {
  Confirmation,
  ConfirmationService,
  NavItemsService,
  UserMenuService,
} from '@abp/ng.theme.shared';
import { Notification } from './notification/notification.component';
import { ConfigStateService, LocalizationService } from '@abp/ng.core';
import { ImpersonationService } from './shared/impersonation/impersonation.service';

@Component({
    selector: 'app-root',
    changeDetection: ChangeDetectionStrategy.OnPush,
    template: `
    <abp-loader-bar></abp-loader-bar>
    @if (impersonation.isImpersonating()) {
      <div class="impersonation-banner">
        <span>
          <i class="fa fa-user-secret me-2" aria-hidden="true"></i>
          {{ impersonatingMessage() }}
        </span>
        <button type="button" class="btn btn-sm btn-light" (click)="stopImpersonation()">
          {{ backToMyAccountLabel() }}
        </button>
      </div>
    }
    <abp-dynamic-layout></abp-dynamic-layout>
    <abp-gdpr-cookie-consent></abp-gdpr-cookie-consent>
    <abp-internet-status></abp-internet-status>
  `,
    standalone: false
})
export class AppComponent implements AfterViewInit, OnInit {
  /*
   * The banner texts are computed from this, so they are re-read after a language switch: the root
   * is OnPush, and nothing else in its template changes then (#44). Starting and stopping
   * impersonation reload the page, so isImpersonating() needs no signal of its own.
   */
  private readonly language = toSignal(inject(LocalizationService).languageChange$);

  /** Banner text naming the account currently being viewed. */
  readonly impersonatingMessage = computed(() => {
    this.language();
    return this.localizationService.instant(
      { key: '::ImpersonatingUser', defaultValue: 'You are viewing the app as {0}' },
      this.impersonation.impersonatedUserName
    );
  });

  readonly backToMyAccountLabel = computed(() => {
    this.language();
    return this.localizationService.instant({
      key: '::BackToMyAccount',
      defaultValue: 'Back to my account',
    });
  });

  stopImpersonation() {
    this.impersonation.stop();
  }

  constructor(
    public impersonation: ImpersonationService,
    private router: Router,
    private renderer: Renderer2,
    private settingTabs: SettingTabsService,
    private userMenu: UserMenuService,
    private confirmationService: ConfirmationService,
    private navItems: NavItemsService,
    private localizationService: LocalizationService,
    private configState: ConfigStateService
  ) {
    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        this.handleRouteChange(event.urlAfterRedirects);
      });

    settingTabs.add([
      {
        name: '::GeneralSettings',
        order: 1,
        requiredPolicy: 'AbpAccount.SettingManagement',
        component: GeneralSettingsComponent,
      },
    ]);

    navItems.addItems([
      {
        id: 'Notification',
        order: 2,
        component: Notification,
      },
    ]);

    const logoutId = 'Logout';
    const logoutItem = userMenu.items.find(item => item.id === logoutId);

    // exclude unwanted user menu items
    const excludeIds = ['Gdpr.GdprNavigation', 'LinkedAccounts', 'AuthorityDelegation'];
    excludeIds.forEach(item => {
      userMenu.removeItem(item);
    });

    // patch logout item
    userMenu.patchItem(logoutId, {
      action: () => {
        this.confirmationService
          .warn('::LogoutConfirmationMessage', '::AreYouSure', {
            messageLocalizationParams: [],
          })
          .subscribe((status: Confirmation.Status) => {
            if (status === Confirmation.Status.confirm) {
              logoutItem.action();
            }
          });
      },
    });

    this.trackTextDirection();
  }

  /**
   * Mirrors the current culture's text direction into localStorage.
   *
   * This used to call `AbpApplicationConfigurationService.get({ includeLocalizationResources: true })`
   * on init and again on every language change — a second fetch of
   * `/api/abp/application-configuration`, in its heaviest variant, for a single boolean that ABP's
   * APP_INITIALIZER has already put in `ConfigStateService`. Reading the store instead removes that
   * request from the boot path. `getDeep$` re-emits whenever the config state is refreshed, so it
   * covers language switches on its own and the old `languageChange$` subscription is redundant.
   */
  private trackTextDirection() {
    this.configState
      .getDeep$('localization.currentCulture.isRightToLeft')
      .subscribe((isRightToLeft: boolean | undefined) => {
        if (isRightToLeft === undefined) return;
        localStorage.setItem('isRightToLeft', `${isRightToLeft}`);
      });
  }

  ngOnInit(): void {
    // The free LeptonX Lite theme applies NO `.lpx-theme-*` class by default (the commercial
    // theme's theme-switcher is gone). Without a theme class neither Lite's light palette nor our
    // `:root .lpx-theme-light` logo override (--lpx-logo) activate, so the UI falls back to a dark
    // look with the stock LEPTON logo. Force the light theme on <body> to restore the ReturnLy logo
    // and the light palette.
    this.renderer.addClass(document.body, 'lpx-theme-light');
  }

  ngAfterViewInit() {
    // The free LeptonX Lite theme — unlike the commercial LeptonX — does not remove the
    // index.html splash loader (#lp-page-loader). Remove it once the app shell has rendered,
    // otherwise the full-screen spinner overlay stays up forever.
    document.getElementById('lp-page-loader')?.remove();
    this.checkAndClickPaymentLink();
  }

  handleRouteChange(url: string) {
    if (url.includes('/account/manage')) {
      this.checkAndClickPaymentLink();
    }
  }

  checkAndClickPaymentLink() {
    const hash = window.location.hash;
    if (hash?.toLowerCase() === '#payment') {
      setTimeout(() => {
        const navTab = document.getElementById('nav-tab');
        
        if (navTab) {
          const links = navTab.querySelectorAll('li a');
          links.forEach(link => {
            const linkText = (link as HTMLAnchorElement).textContent?.trim();
            const PaymentLabel = this.localizationService.instant('::PaymentTab');
            if ( linkText === PaymentLabel) {
              (link as HTMLAnchorElement).click();
            }
          });
        }
      }, 0);
    }
  }
}
